import React from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Alert from '@material-ui/lab/Alert';
import { QRCodeSVG } from 'qrcode.react';

import { useTranslation } from 'react-i18next';
import { languageMap } from '../LanguageSelect';
import { TokenTypeSelect } from '../FormWidgets/Select';
import { useAddress } from '../Hooks/addressHook';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useSnackbar } from 'notistack';
import { useAuth } from '../Hooks/authHook';

import { TotpInput } from '../FormWidgets/TotpInput';
import { InfoButton } from '../FormWidgets/Description';
import { statusColors } from '../Constants/constants';
import TelegramDialog, { TelegramDestination } from './Account/TelegramDialog';

import base32Encode from 'base32-encode';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: '2rem',
    padding: '2rem',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) }
  },
  section: { padding: theme.spacing(3), height: '100%', boxSizing: 'border-box' },
  accountDivider: { margin: theme.spacing(3, 0) },
  deleteAccount: { color: theme.palette.error.main, borderColor: 'currentColor' }
}));

export default function AccountPage({ user, setUser }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const address = useAddress();
  const showTelegram = !address.localMode && Boolean(user.telegram);
  const showAlarmEmail = !address.localMode && user.alarm_email_enabled !== undefined;
  const [savingTelegram, setSavingTelegram] = React.useState(false);
  const [savingAlarmEmail, setSavingAlarmEmail] = React.useState(false);
  const [telegramError, setTelegramError] = React.useState(false);
  const [alarmEmailError, setAlarmEmailError] = React.useState(false);
  const pendingPreferences = React.useRef({ telegram: false, email: false });
  const mounted = React.useRef(false);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const saveTelegramEnabled = async (enabled) => {
    if (pendingPreferences.current.telegram || !user.telegram.chat_id) return;
    pendingPreferences.current.telegram = true;
    setSavingTelegram(true);
    setTelegramError(false);
    try {
      const response = await fetch(address.api_url('/api/set_telegram'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, chat_id: user.telegram.chat_id })
      });
      if (!response.ok) throw new Error('Telegram preference request failed');
      const telegram = await response.json();
      if (mounted.current) setUser((previous) => ({ ...previous, telegram }));
    } catch (failure) {
      if (mounted.current) setTelegramError(true);
    } finally {
      pendingPreferences.current.telegram = false;
      if (mounted.current) setSavingTelegram(false);
    }
  };

  const saveAlarmEmailEnabled = async (enabled) => {
    if (pendingPreferences.current.email || !user.email) return;
    pendingPreferences.current.email = true;
    setSavingAlarmEmail(true);
    setAlarmEmailError(false);
    try {
      const response = await fetch(address.api_url('/api/set_alarm_email'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      if (!response.ok) throw new Error('Alarm email preference request failed');
      const result = await response.json();
      if (mounted.current)
        setUser((previous) => ({ ...previous, alarm_email_enabled: result.alarm_email_enabled }));
    } catch (failure) {
      if (mounted.current) setAlarmEmailError(true);
    } finally {
      pendingPreferences.current.email = false;
      if (mounted.current) setSavingAlarmEmail(false);
    }
  };

  const [api_tokens, setApiTokens] = React.useState([]);

  React.useEffect(() => {
    if (!address.localMode) {
      fetch('api/api_token_infos')
        .then((response) => response.json())
        .then((data) => setApiTokens(data))
        .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));
    }
  }, []);

  const [open_email_dialog, setOpenEmailDialog] = React.useState(false);
  const openEmailDialog = () => {
    setOpenEmailDialog(true);
  };
  const closeEmailDialog = () => {
    setOpenEmailDialog(false);
  };

  const [open_telegram_dialog, setOpenTelegramDialog] = React.useState(false);
  const [telegramTestRetryAt, setTelegramTestRetryAt] = React.useState(0);

  const [open_language_dialog, setOpenLanguageDialog] = React.useState(false);
  const openLanguageDialog = () => {
    setOpenLanguageDialog(true);
  };
  const closeLanguageDialog = () => {
    setOpenLanguageDialog(false);
  };

  const [open_delete_dialog, setOpenDeleteDialog] = React.useState(false);
  const openDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };
  const closeDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const [open_password_dialog, setOpenPasswordDialog] = React.useState(false);
  const openPasswordDialog = () => {
    setOpenPasswordDialog(true);
  };
  const closePasswordDialog = () => {
    setOpenPasswordDialog(false);
  };

  const [open_2faDialog, setOpen2faDialog] = React.useState(false);
  const [_2fa_authenticator_url, set2fAuthenticatorUrl] = React.useState('');
  const [_2fa_recovery, set2faRecovery] = React.useState('');
  const open2faDialog = () => {
    fetch('api/set_2fa_secret', {
      method: 'POST',
      body: JSON.stringify({ enable: 'true' })
    })
      .then((data) => {
        data.json().then((data_json) => {
          let hex_secret_str = data_json['hex_secret'];

          let arr = [];
          let hex_secret_str_split = hex_secret_str.split('');
          for (let i = 0; i < hex_secret_str_split.length; i += 2) {
            arr.push(Number('0x' + hex_secret_str_split[i] + hex_secret_str_split[i + 1]));
          }
          let hex_secret_buffer = new Uint8Array(arr);

          let base32_secret = base32Encode(hex_secret_buffer, 'RFC3548');

          let base32_secret_replace = base32_secret.replaceAll('=', '');
          set2fAuthenticatorUrl(
            `otpauth://totp/GrowControl:${user.name}?secret=${base32_secret_replace}`
          );
          set2faRecovery(data_json['recovery']);

          setOpen2faDialog(true);
        });
      })
      .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));
  };
  const close2faDialog = () => {
    setOpen2faDialog(false);
  };

  const [tokenDialogData, setTokenDialogData] = React.useState({ req_type: '', req: '' });
  const [tokenDialogPayload, setTokenDialogPayload] = React.useState({});

  const [open_token_dialog, setOpenTokenDialog] = React.useState(false);
  const openTokenDialog = () => {
    setOpenTokenDialog(true);
  };
  const closeTokenDialog = () => {
    setOpenTokenDialog(false);
  };

  return (
    <React.Fragment>
      <Container className={classes.root}>
        <EmailDialog
          open={open_email_dialog}
          handleClose={closeEmailDialog}
          user={user}
          setUser={setUser}
        />
        {showTelegram && open_telegram_dialog && (
          <TelegramDialog
            handleClose={() => setOpenTelegramDialog(false)}
            user={user}
            setUser={setUser}
            testRetryAt={telegramTestRetryAt}
            setTestRetryAt={setTelegramTestRetryAt}
          />
        )}
        <LanguageDialog open={open_language_dialog} handleClose={closeLanguageDialog} />
        <PasswordDialog open={open_password_dialog} handleClose={closePasswordDialog} />
        <DeleteDialog open={open_delete_dialog} handleClose={closeDeleteDialog} user={user} />
        <TokenDialog
          open={open_token_dialog}
          handleClose={closeTokenDialog}
          params={tokenDialogData}
          payload={tokenDialogPayload}
        />
        <_2FADialog
          open={open_2faDialog}
          handleClose={close2faDialog}
          authenticator_url={_2fa_authenticator_url}
          recovery={_2fa_recovery}
          user={user}
          setUser={setUser}
        />
        <React.Fragment>
          <Typography variant="h4" gutterBottom>
            {t('my_account')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" className={classes.section}>
                <Typography component="h2" variant="h6" gutterBottom>
                  {t('account_security')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography gutterBottom>{t('username')}</Typography>
                    <Typography gutterBottom>
                      <i>{user.name}</i>
                    </Typography>
                    <Button onClick={openPasswordDialog} color="primary">
                      {t('change_password')}
                    </Button>
                    <Button onClick={open2faDialog} color="primary">
                      {t(!user['_2fa_enabled'] ? 'enable2fa' : 'disable2fa')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography gutterBottom>E-Mail</Typography>
                    <Typography gutterBottom>
                      <i>{user.email || t('not_set')}</i>
                    </Typography>
                    {!address.localMode && (
                      <Button onClick={openEmailDialog} color="primary">
                        {t('change_email')}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" className={classes.section}>
                <Typography component="h2" variant="h6" gutterBottom>
                  {t('account_language')}
                </Typography>
                <Typography gutterBottom>
                  <i>{user.language !== '' ? t(user.language) : t('not_set')}</i>
                </Typography>
                <Button onClick={openLanguageDialog} color="primary">
                  {t('save_current_language')}
                </Button>
              </Paper>
            </Grid>
            {(showAlarmEmail || showTelegram) && (
              <Grid item xs={12}>
                <Paper variant="outlined" className={classes.section}>
                  <Typography component="h2" variant="h6" gutterBottom>
                    {t('alarm_notifications')}
                  </Typography>
                  <Grid container spacing={3}>
                    {showAlarmEmail && (
                      <Grid item xs={12} sm={6}>
                        <Typography gutterBottom>
                          E-Mail{' '}
                          <InfoButton
                            content={t('alarm_email_notifications_help')}
                            img=""
                            brief={t('alarm_email_notifications')}
                            ariaLabel={t('alarm_email_notifications_help_label')}
                          />
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              checked={Boolean(user.email) && (user.alarm_email_enabled ?? true)}
                              disabled={!user.email || savingAlarmEmail}
                              onChange={(event) => saveAlarmEmailEnabled(event.target.checked)}
                            />
                          }
                          label={t('alarm_email_notifications')}
                        />
                        {alarmEmailError && (
                          <Alert severity="error">{t('alarm_email_change_error')}</Alert>
                        )}
                      </Grid>
                    )}
                    {showTelegram && (
                      <Grid item xs={12} sm={6}>
                        <Typography gutterBottom>
                          Telegram{' '}
                          <InfoButton
                            content={t('telegram_notifications_help')}
                            img=""
                            brief="Telegram"
                            ariaLabel={t('telegram_notifications_help_label')}
                          />
                        </Typography>
                        <TelegramDestination telegram={user.telegram} />
                        <div>
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                checked={
                                  Boolean(user.telegram.chat_id) && (user.telegram.enabled ?? true)
                                }
                                disabled={!user.telegram.chat_id || savingTelegram}
                                onChange={(event) => saveTelegramEnabled(event.target.checked)}
                              />
                            }
                            label={t('telegram_notifications')}
                          />
                        </div>
                        {telegramError && (
                          <Alert severity="error">{t('telegram_change_error')}</Alert>
                        )}
                        <Button
                          onClick={() => setOpenTelegramDialog(true)}
                          disabled={savingTelegram}
                          color="primary"
                        >
                          {t('change_telegram')}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>
          <Divider className={classes.accountDivider} />
          <Button onClick={openDeleteDialog} variant="outlined" className={classes.deleteAccount}>
            {t('delete_account')}
          </Button>
        </React.Fragment>
      </Container>

      {!address.localMode && (
        <Container className={classes.root}>
          <React.Fragment>
            <Typography variant="h5" gutterBottom>
              API TOKENS
            </Typography>
            <Grid container spacing={7}>
              <Grid item xs={6}>
                {api_tokens.length === 0 && <Typography gutterBottom>{t('no-tokens')}</Typography>}
                {api_tokens.length > 0 &&
                  api_tokens.map((token_type, index) => (
                    <>
                      <Typography gutterBottom>
                        Token Type: <i>{token_type}</i>
                      </Typography>
                      <Button
                        onClick={() => {
                          setTokenDialogData((prev) => ({ req_type: 'POST', req: 'api_token' }));
                          setTokenDialogPayload((prev) => ({
                            type: token_type,
                            username: '',
                            password: ''
                          }));
                          openTokenDialog();
                        }}
                        color="primary"
                      >
                        {t('api_token')}
                      </Button>
                      <Button
                        onClick={() => {
                          setTokenDialogData((prev) => ({
                            req_type: 'POST',
                            req: 'delete_api_token'
                          }));
                          setTokenDialogPayload((prev) => ({
                            type: token_type,
                            username: '',
                            password: ''
                          }));
                          openTokenDialog();
                        }}
                        color="secondary"
                      >
                        {t('delete_api_token')}
                      </Button>
                    </>
                  ))}
              </Grid>
              <Grid item xs={6}>
                <Button
                  onClick={() => {
                    setTokenDialogData((prev) => ({ req_type: 'POST', req: 'create_api_token' }));
                    setTokenDialogPayload((prev) => ({
                      type: '',
                      username: '',
                      password: '',
                      date: ''
                    }));
                    openTokenDialog();
                  }}
                  color="primary"
                >
                  {t('create_api_token')}
                </Button>
                <Button color="primary" target="_blank" href='/api/v1/ref'>
                  {t('api_ref')}
                </Button>
              </Grid>
            </Grid>
          </React.Fragment>
        </Container>
      )}
    </React.Fragment>
  );
}

function EmailDialog({ open, handleClose, user, setUser }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const address = useAddress();
  const [values, setValues] = React.useState({ email: '' });

  const validateForm = () => {
    if (values.email) {
      fetch(address.api_url('/api/set_email'), { method: 'POST', body: values.email }).then(
        (response) => {
          if (response.ok) {
            enqueueSnackbar(t('email_change_success') + ': ' + values.email, {
              variant: 'success'
            });
            setUser((previous) => ({ ...previous, email: values.email }));
          } else {
            enqueueSnackbar(t('email_change_error_2') + ': ' + values.email, { variant: 'info' });
          }
        }
      );
      handleClose();
    } else {
      handleClose();
    }
  };

  const deleteEmail = () => {
    fetch(address.api_url('/api/set_email?remove'), { method: 'POST', body: '' }).then(
      (response) => {
        if (response.ok) {
          enqueueSnackbar(t('email_remove_success'), { variant: 'success' });
          setUser((previous) => ({ ...previous, email: '' }));
        } else {
          enqueueSnackbar(t('email_remove_error') + ': ' + values.email, { variant: 'error' });
        }
      }
    );
    handleClose();
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={validateForm}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('set_email')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label="E-Mail"
          type="text"
          fullWidth
          onChange={(event) => setValues({ ...values, email: event.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={validateForm} color="primary">
          {t('set')}
        </Button>
        <Button onClick={deleteEmail} color="primary">
          {t('delete')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function LanguageDialog({ open, handleClose }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const cur_language = localStorage.getItem('i18nextLng') || 'de';
  const address = useAddress();

  const setLanguage = () => {
    fetch(address.api_url('/api/set_language'), { method: 'POST', body: cur_language }).then(
      (response) => {
        if (response.ok) {
          window.location.reload(false);
        } else {
          enqueueSnackbar(t('language_set_error'), { variant: 'error' });
        }
      }
    );
    handleClose();
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={setLanguage}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('set_language')}</DialogTitle>
      <DialogContent>
        {t('set_language_confirm_1')} <i>{languageMap[cur_language].label}</i>{' '}
        {t('set_language_confirm_2')}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={setLanguage} color="primary">
          {t('set')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function PasswordDialog({ open, handleClose }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const address = useAddress();
  const [password, setPassword] = React.useState(null);
  const [password_2, setPassword2] = React.useState(null);

  const validateForm = (event) => {
    if (password && password_2) {
      if (password !== password_2) {
        enqueueSnackbar(t('set_password_error_missmatch'), { variant: 'error' });
        return;
      }

      const request = { password: password };
      fetch(address.api_url('/api/set_new_password'), {
        method: 'POST',
        body: JSON.stringify(request)
      }).then((response) => {
        if (response.ok) {
          enqueueSnackbar(t('set_password_success'), { variant: 'info' });
        } else if (response.status === 401) {
          enqueueSnackbar(t('set_password_error_unauthenticated'), { variant: 'error' });
        } else if (response.status === 403) {
          enqueueSnackbar(t('set_password_error_strength'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('set_password_error_unknown'), { variant: 'error' });
        }
      });
      handleClose();
    } else {
      handleClose();
    }
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={validateForm}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('set_password')}</DialogTitle>
      <DialogContent>
        <Typography paragraph style={{ color: statusColors.bad }}>
          {t('change_password_controller_warning')}
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label="new password"
          type="password"
          fullWidth
          onChange={(event) => setPassword(event.target.value)}
        />
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label="repeat password"
          type="password"
          fullWidth
          onChange={(event) => setPassword2(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={validateForm} color="primary">
          {t('set')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function _2FADialog({ open, handleClose, authenticator_url, recovery, user, setUser }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const auth = useAuth();

  const [state, setState] = React.useState(1);
  const [otpValidErr, setOtpValidErr] = React.useState(false);
  const [otp, setOtp] = React.useState('');

  let enable = !user['_2fa_enabled'];

  const saveOtp = (e) => {
    setOtp(e.target.value);
  };

  const next = () => {
    if (state === 1 && enable) {
      setState(2);
    } else if (state == 2 && enable) {
      setState(3);
    } else {
      fetch('api/set_2fa', {
        method: 'POST',
        body: JSON.stringify({ totp: Number(otp), enable: enable })
      })
        .then((data) => {
          data.json().then((json) => {
            if (json.status == 'error' && json.error_msg == 'error totp validation') {
              setOtp('');
              setOtpValidErr(true);
            } else {
              enqueueSnackbar(t(enable ? 'enable_2fa_success' : 'disable_2fa_success'), {
                variant: 'success'
              });
              setUser({ ...user, _2fa_enabled: enable });
              reset();
              handleClose();
            }
          });
        })
        .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));
    }
  };

  const reset = () => {
    setState(1);
    setOtp('');
    setOtpValidErr(false);
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={next}
      enterActionDisabled={state == 3 && String(otp).length !== 6}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('setting_2fa')}</DialogTitle>
      <DialogContent>
        {enable ? (
          <>
            {state == 1 && (
              <>
                <div>
                  {t('instructions_2fa_1')}
                  <div
                    style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}
                  >
                    <QRCodeSVG value={authenticator_url} width="400" height="400" />
                  </div>
                </div>
              </>
            )}
            {state == 2 && (
              <>
                <div>{t('_2fa_recovery_install_instructions')}</div>

                <div style={{ marginTop: '2rem', fontWeight: 'bold' }}>{recovery}</div>
              </>
            )}
            {state == 3 && (
              <>
                <TotpInput otp={otp} saveOtp={saveOtp} err={otpValidErr} />
              </>
            )}
          </>
        ) : (
          <div>
            <h3>{t('disable2fa')}</h3>
            <TotpInput otp={otp} saveOtp={saveOtp} err={otpValidErr} />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => next()}
          color="primary"
          disabled={state == 3 && String(otp).length !== 6}
        >
          {t(state < 3 && enable ? 'continue' : 'save')}
        </Button>
        <Button
          onClick={() => {
            reset();
            handleClose();
          }}
          color="secondary"
        >
          {t('cancel')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function DeleteDialog({ open, handleClose, user }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const auth = useAuth();
  const classes = useStyles();

  const deleteAccount = () => {
    auth.deleteAccount((m) => {
      if (m === 'good') history.replace('/login');
      else enqueueSnackbar(t(m), { variant: 'error' });
    });
    handleClose();
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={deleteAccount}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('delete_account')}</DialogTitle>
      <DialogContent>{t('delete_account_confirm')}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={deleteAccount} className={classes.deleteAccount}>
          {t('delete')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function TokenDialogField({ id, value, data, addData }) {
  let { t } = useTranslation();

  if (id !== 'type') {
    return (
      <TextField
        autoFocus
        margin="dense"
        id="serial"
        key={id}
        value={data[id] === undefined || data[id] === '' ? value : data[id]}
        label={id === 'date' ? 'Valid for X Days' : t(id)}
        type={id === 'password' ? 'password' : id === 'date' ? 'number' : 'text'}
        fullWidth
        onChange={(event) =>
          addData(id, id === 'date' ? parseInt(event.target.value) : event.target.value)
        }
      />
    );
  } else {
    return (
      <TokenTypeSelect
        has_write={false}
        value={data[id] === undefined || data[id] === '' ? value : data[id]}
        onChange={(event) => addData(id, event.target.value)}
      />
    );
  }
}

/**
 * @param[in] params (req_type (GET, POST...); req f.e. "/api/create_api_token)
 * @param[in] payload f.e. "{'password':'', 'username':''}"
 */
function TokenDialog({ open, handleClose, params, payload }) {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  const address = useAddress();
  const [data, setData] = React.useState(payload);

  // Make sure payload is acctually changed by overwriting old data.
  React.useEffect(() => {
    setData((prev) => ({ ...prev, ...payload }));
  }, [payload]);

  // Make sure data is updated and dialog is rerendered.
  const addData = (key, value) => {
    let new_data = {};
    new_data[key] = value;
    setData((prev) => ({ ...prev, ...new_data }));
  };

  const validateForm = (event) => {
    // Make sure all data is filled.
    let all_exists = true;
    for (const [key, value] of Object.entries(data)) {
      if (value === '') {
        all_exists = false;
        break;
      }
    }

    if (all_exists) {
      fetch(address.api_url('/api/' + params.req), {
        method: params.req_type,
        body: JSON.stringify(data)
      }).then((response) => {
        if (response.ok) {
          if (params.req === 'api_token') {
            response.text().then((text) => navigator.clipboard.writeText(text));
            enqueueSnackbar('api token "' + data.type + '" copied to clipboard!', {
              variant: 'success'
            });
          } else if (params.req === 'create_api_token' || params.req === 'delete_api_token') {
            window.location.reload();
          }
        } else {
          response.text().then((text) => {
            enqueueSnackbar(params.req + ': ' + response.status + ', ' + text, {
              variant: 'error'
            });
          });
        }
      });
      handleClose();
    } else {
      enqueueSnackbar('Please fill all fields!', { variant: 'error' });
    }
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={validateForm}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t(params.req)}</DialogTitle>
      <DialogContent>
        {Object.entries(payload).map(([key, value]) => (
          <TokenDialogField id={key} value={value} data={data} addData={addData} />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={validateForm} color="primary">
          {t('set')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
